import { isString } from 'framework/script_utilities/type-guards';
import { errorReplacer } from './error-utils';

export class ErrorHandler {
  protected loggingEnabled = false;
  protected fallbackTitle = null;
  protected fallbackMessage = null;
  protected suppressionHandler: (error) => boolean = () => true;
  protected customPresenters: { errorType?: ErrorType<unknown>; predicate?: ErrorPredicate<unknown>; presenter: ErrorPresenter<unknown> }[] = [];
  protected customAdapters: { errorType: ErrorType<unknown>; adapter: ErrorInfoAdapter<unknown> }[] = [];
  protected commonAdapters: ErrorInfoAdapter<unknown>[] = [];
  protected defaultPresenter: ErrorInfoPresenter = null;

  enableLogging(): this {
    this.loggingEnabled = true;
    return this;
  }

  useFallbackTitle(title: string): this {
    this.fallbackTitle = title;
    return this;
  }

  useFallbackMessage(message: string): this {
    this.fallbackMessage = message;
    return this;
  }

  suppressEqualErrors(timeout = 1000, comparer = this.errorsPropertiesComparer): this {
    let lastErrorShown = null;
    let lastShownTime: number = null;

    this.suppressionHandler = (error) => {
      const time = new Date().getTime();
      if (lastErrorShown != null && comparer(lastErrorShown, error) && (time - lastShownTime <= timeout))
        return false;
      lastErrorShown = error;
      lastShownTime = time;
      return true;
    };

    return this;
  }

  handleWindowErrors(): this {
    window.addEventListener('error', e => this.processError(e.error || e));
    return this;
  }

  handleUnhandledRejections(): this {
    window.addEventListener('unhandledrejection', e => this.processError(e.reason));
    return this;
  }

  handleRejectionsHandled(): this {
    window.addEventListener('rejectionhandled', e => this.processError(e.reason));
    return this;
  }

  addErrorTypePresenter<T>(errorType: ErrorType<T>, presenter: ErrorPresenter<T>): this {
    this.customPresenters.push({ errorType: errorType, presenter });
    return this;
  }

  addPredicatePresenter<T>(predicate: ErrorPredicate<T>, presenter: ErrorPresenter<T>): this {
    this.customPresenters.push({ predicate: predicate, presenter });
    return this;
  }

  addErrorTypeAdapter<T>(errorType: ErrorType<T>, adapter: ErrorInfoAdapter<T>): this {
    this.customAdapters.push({ errorType, adapter });
    return this;
  }

  addCommonAdapter(adapter: ErrorInfoAdapter<unknown>): this {
    this.commonAdapters.push(adapter);
    return this;
  }

  useDefaultPresenter(presenter: ErrorInfoPresenter): this {
    this.defaultPresenter = presenter;
    return this;
  }

  protected getInfo(error) {
    const info = new ErrorInfo();
    info.message = isString(error) ? error : error.message || this.fallbackMessage;
    info.title = error.title || this.fallbackTitle;
    info.ambient = !!error.ambient;
    info.details = error;
    return info;
  }

  protected applyAdapters(error, info: ErrorInfo) {
    function applyAdapter(adapter: ErrorInfoAdapter<unknown>) {
      const changes = adapter(error, info);
      if (changes) Object.assign(info, changes);
    }

    for (const ca of this.customAdapters)
      if (error instanceof ca.errorType)
        applyAdapter(ca.adapter);

    for (const adapter of this.commonAdapters)
      applyAdapter(adapter);
  }

  protected processError(error) {
    if (this.loggingEnabled)
      this.logError(error);
    if (this.suppressionHandler(error))
      this.showError(error);
  }

  logError(error) {
    console.error(error);
  }

  showError(error) {
    for (const cp of this.customPresenters)
      if (cp.errorType != null && error instanceof cp.errorType || cp.predicate != null && cp.predicate(error)) {
        cp.presenter(error);
        return;
      }

    const info = this.getInfo(error);
    this.applyAdapters(error, info);
    if (this.defaultPresenter)
      this.defaultPresenter(info);
  }

  standardConfiguration(): this {
    return this
      .handleWindowErrors()
      .handleUnhandledRejections()
      .useFallbackTitle('Something went wrong')
      .useFallbackMessage('Sorry, we\'ve run into a totally unexpected issue.')
      .suppressEqualErrors();
  }

  errorsMessageComparer = (x, y) => {
    return x.message === y.message;
  }

  errorsPropertiesComparer = (x, y) => {
    return JSON.stringify(x, errorReplacer) === JSON.stringify(y, errorReplacer);
  }
}

export class ErrorInfo {
  message: string;
  title: string;
  details: object;
  ambient: boolean;
}

export type ErrorType<T> = { new(...params: unknown[]): T };
export type ErrorPresenter<T> = (error: T) => void;
export type ErrorPredicate<T> = (error: T) => boolean;
export type ErrorInfoAdapter<T> = (error: T, info: ErrorInfo) => Partial<ErrorInfo>;
export type ErrorInfoPresenter = (info: ErrorInfo) => void;
